import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import type { ItemStatus, ItemTipo } from '@central/types';
import { useItemsList } from '@/hooks/useItems';
import { useFavoriteIds } from '@/hooks/useFavorites';
import { ItemStatusBadge, TIPO_LABELS } from '@/components/ItemStatusBadge';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos os status' },
  { value: 'em-producao', label: 'Em produção' },
  { value: 'publicado', label: 'Publicado' },
  { value: 'novo-revisar', label: 'Novo — revisar' },
  { value: 'rascunho', label: 'Rascunho' },
  { value: 'pausado', label: 'Pausado' },
  { value: 'arquivado', label: 'Arquivado' },
];

export function CategoriaPage(): JSX.Element {
  const { tipo } = useParams<{ tipo: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const filtroStatus = searchParams.get('status') ?? '';
  const filtroTag = searchParams.get('tag') ?? '';
  const filtroBusca = searchParams.get('q') ?? '';

  const { data, isLoading } = useItemsList({
    tipo: tipo as ItemTipo,
    status: filtroStatus || undefined,
    limit: 500,
  });
  const { data: favData } = useFavoriteIds();
  const favoriteIds = new Set(favData?.ids ?? []);

  const items = useMemo(() => {
    let list = data?.items ?? [];
    if (filtroTag) list = list.filter((i) => i.tags.includes(filtroTag));
    if (filtroBusca) {
      const q = filtroBusca.toLowerCase();
      list = list.filter(
        (i) =>
          i.nome.toLowerCase().includes(q) ||
          i.descricao?.toLowerCase().includes(q) ||
          i.url?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [data, filtroTag, filtroBusca]);

  const allTags = useMemo(() => {
    const set = new Set<string>();
    for (const item of data?.items ?? []) {
      for (const t of item.tags) set.add(t);
    }
    return Array.from(set).sort();
  }, [data]);

  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  }

  const titulo = TIPO_LABELS[tipo ?? ''] ?? tipo;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <div>
          <nav className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Dashboard</Link> › {titulo}
          </nav>
          <h1 className="text-2xl font-semibold">{titulo}</h1>
          <p className="text-sm text-muted-foreground">
            {isLoading ? 'Carregando…' : `${items.length} item${items.length !== 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 items-center">
        <Input
          placeholder="Buscar por nome, descrição ou URL…"
          value={filtroBusca}
          onChange={(e) => setParam('q', e.target.value)}
          className="max-w-xs"
        />
        <select
          value={filtroStatus}
          onChange={(e) => setParam('status', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {allTags.length > 0 && (
          <select
            value={filtroTag}
            onChange={(e) => setParam('tag', e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">Todas as tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>#{t}</option>
            ))}
          </select>
        )}
        {(filtroStatus || filtroTag || filtroBusca) && (
          <button
            type="button"
            onClick={() => setSearchParams({})}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Tabela */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Status</th>
              <th className="text-left px-3 py-2 font-medium">Nome</th>
              <th className="text-left px-3 py-2 font-medium hidden md:table-cell">URL</th>
              <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">Stack</th>
              <th className="text-left px-3 py-2 font-medium hidden lg:table-cell">Tags</th>
              <th className="text-right px-3 py-2 font-medium">Atualizado</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  Carregando…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                  Nenhum item. Crie um com "+ Adicionar" no topo.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr
                  key={item.id}
                  className="border-t border-border hover:bg-accent/30 transition-colors"
                >
                  <td className="px-3 py-2">
                    <ItemStatusBadge status={item.status as ItemStatus} compact />
                  </td>
                  <td className="px-3 py-2">
                    <Link to={`/items/${item.id}`} className="font-medium hover:text-primary">
                      {favoriteIds.has(item.id) && <span className="mr-1">⭐</span>}
                      {item.nome}
                    </Link>
                    {item.descricao && (
                      <p className="text-xs text-muted-foreground line-clamp-1">{item.descricao}</p>
                    )}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    {item.url ? (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary max-w-[220px] truncate"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {item.url.replace(/^https?:\/\//, '')}
                      </a>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {item.tech_stack.slice(0, 2).map((s) => (
                        <Badge key={s} variant="outline" className="text-[10px]">{s}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {item.tags.slice(0, 3).map((t) => (
                        <Badge key={t} variant="secondary" className="text-[10px]">#{t}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">
                    {new Date(item.atualizado_em).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
